<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, type Ref, ref, watch, type WatchStopHandle } from 'vue';
import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid';
import { BuildingOffice2Icon, HomeModernIcon, MapIcon, MapPinIcon } from '@heroicons/vue/24/outline';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/vue';
import { useDebounceFn, useGeolocation } from '@vueuse/core';
import { useRouter } from 'vue-router';

import { useUserStore } from '@/plugins/pinia/userStore.ts';
import { useSearchFiltersStore } from '@/views/search/searchFiltersStore.ts';
import { type AutocompleteEntity, type AutocompleteResult, type AutocompleteSuggestions, type LocationKind } from '@/views/search/types.ts';
import { fetchSearchAutocomplete } from '@/views/search/index.ts';
import { useI18n } from 'vue-i18n';
import { useGoogleTagManager } from '@/plugins/analytics/google-tag-manager';
import { loadPresentationConfiguration } from '@/utilities/presentation-configuration.ts';

type SelectableItem = AutocompleteResult | AutocompleteEntity;

const { placeholder } = defineProps<{ placeholder?: string }>();

const { t } = useI18n();

const router = useRouter();
const userStore = useUserStore();
const filters = useSearchFiltersStore();

const emptySuggestions: AutocompleteSuggestions = { locations: [], projects: [], builders: [] };

const searchText: Ref<string> = ref('');
const suggestions: Ref<AutocompleteSuggestions> = ref(emptySuggestions);
const hasSuggestions = computed(() => suggestions.value.locations.length > 0 || suggestions.value.projects.length > 0 || suggestions.value.builders.length > 0);
const selectedAutocompleteItem: Ref<SelectableItem | null> = ref(filters.autocomplete);
watch(
  () => filters.autocomplete,
  (value) => {
    selectedAutocompleteItem.value = value ?? null;
  }
);
let isWatchingCoords = false;
let stopCoordsWatch: WatchStopHandle | undefined;
const isFocused = ref(false);
const canRequestPosition = ref(false);
const showNearMe = computed(() => isFocused.value && canRequestPosition.value && searchText.value.length === 0);
let locationSuggestionLimit: number | null = null;

const autocomplete = useDebounceFn(async () => {
  if (!searchText.value || searchText.value.length < 1) {
    suggestions.value = emptySuggestions;
    return;
  }

  if (locationSuggestionLimit === null) {
    const presentation = await loadPresentationConfiguration();
    locationSuggestionLimit = presentation.search.locationSuggestionLimit;
  }

  suggestions.value = await fetchSearchAutocomplete(searchText.value, userStore.coordinates, locationSuggestionLimit);
}, 100);

const startCoordsWatch = () => {
  if (isWatchingCoords) return;

  isWatchingCoords = true;
  canRequestPosition.value = false;
  const { coords } = useGeolocation();
  stopCoordsWatch = watch(coords, (value) => {
    if (value && value.latitude !== Infinity && value.longitude !== Infinity) {
      userStore.setCoordinates({
        latitude: value.latitude,
        longitude: value.longitude,
      });
      return;
    }

    userStore.setCoordinates(undefined);
  });
};

const onFocus = async () => {
  isFocused.value = true;
  if (isWatchingCoords) return;

  if (!('permissions' in navigator)) {
    canRequestPosition.value = true;
    return;
  }

  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    if (status.state === 'granted') {
      startCoordsWatch();
      return;
    }

    canRequestPosition.value = status.state === 'prompt';
  } catch {
    canRequestPosition.value = true;
  }
};

const onBlur = () => {
  isFocused.value = false;
};

const onChange = (event: Event & { target: HTMLInputElement }) => {
  const value = event.target.value;
  searchText.value = value;
  const ga = useGoogleTagManager();
  if (ga) {
    ga.trackSearch(value);
  }
};

const onSelect = () => {
  const item = selectedAutocompleteItem.value;
  if (!item) {
    return;
  }

  if ('location' in item) {
    filters.updateAutocomplete(item);
    return;
  }

  if (item.kind === 'project') {
    void router.push({ name: 'project', params: { slug: item.name.toSlug(), projectId: item.id } });
    return;
  }

  void router.push({ name: 'builder', params: { slug: item.name.toSlug(), builderId: item.id } });
};

const searchActive = () => {
  if (!selectedAutocompleteItem.value) {
    selectedAutocompleteItem.value = suggestions.value.locations[0] ?? suggestions.value.projects[0] ?? suggestions.value.builders[0] ?? null;
  }
  onSelect();
};

const isCity = (kind: LocationKind) => kind === 'city';

const displayName = (item: SelectableItem | null) => {
  if (!item) {
    return '';
  }

  return 'location' in item ? item.location.name : item.name;
};

onMounted(() => {
  watch(searchText, autocomplete);
});

onBeforeUnmount(() => {
  stopCoordsWatch?.();
});
</script>

<template>
  <Combobox v-model="selectedAutocompleteItem" as="div" nullable @update:modelValue="onSelect">
    <div class="relative">
      <ComboboxInput
        :display-value="(item) => displayName(item as SelectableItem | null)"
        :placeholder="placeholder ?? t('views.search.placeholder')"
        aria-autocomplete="none"
        autocomplete="off"
        class="focus:outline-ph-green-600 2xs:text-base 2xs:h-11 2xs:pr-10 h-9 w-full rounded-md border-none bg-white pr-8 pl-3 text-sm text-gray-900 ring-1 ring-gray-300 -outline-offset-1 focus:outline-2 focus:-outline-offset-1"
        data-search-box-input="true"
        @blur="onBlur"
        @change="onChange"
        @focus="onFocus"
      />
      <button class="2xs:h-5 2xs:w-5 2xs:top-3 2xs:right-3.5 absolute top-2.5 right-2.5 h-4 w-4 cursor-pointer text-gray-400 outline-hidden" tabindex="-1" @click="searchActive">
        <MagnifyingGlassIcon aria-hidden="true" />
        <span class="sr-only">{{ t('views.search.button') }}</span>
      </button>
      <div v-if="showNearMe" class="absolute z-20 mt-1 w-full rounded-md bg-white p-0.5 shadow-lg ring-1 ring-gray-300/50">
        <button
          class="hover:bg-ph-green-600 2xs:text-base group flex w-full cursor-pointer items-center rounded-sm p-2 text-left text-sm text-gray-900 select-none hover:text-white"
          type="button"
          @click="startCoordsWatch"
          @mousedown.prevent
        >
          <MapPinIcon aria-hidden="true" class="2xs:h-6 2xs:w-6 h-5 w-5 shrink-0" />
          <span class="2xs:ml-2 ml-1 min-w-0">
            <span class="block truncate">{{ t('views.search.near-me.label') }}</span>
            <span class="block truncate text-xs text-gray-500 group-hover:text-white/80">{{ t('views.search.near-me.description') }}</span>
          </span>
        </button>
      </div>
      <ComboboxOptions v-if="hasSuggestions" as="div" class="focus:outline-ph-green-600 absolute z-20 mt-1 w-full rounded-md bg-white p-0.5 text-base shadow-lg ring-1 ring-gray-300/50">
        <ul class="max-h-96 overflow-auto rounded-sm pr-0.5 focus:outline-hidden">
          <template v-if="suggestions.locations.length > 0">
            <li class="px-2 pt-2 pb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase select-none">{{ t('views.search.sections.locations') }}</li>
            <ComboboxOption v-for="searchItem in suggestions.locations" :key="searchItem.key" v-slot="{ active, selected }" :value="searchItem" as="template">
              <li :class="['2xs:text-base relative flex cursor-pointer items-center rounded-sm p-2 text-sm select-none', active ? 'bg-ph-green-600 text-white' : 'text-gray-900']">
                <MapPinIcon v-if="isCity(searchItem.location.kind)" aria-hidden="true" class="2xs:h-6 2xs:w-6 h-5 w-5 shrink-0" />
                <MapIcon v-else aria-hidden="true" class="2xs:h-6 2xs:w-6 h-5 w-5 shrink-0" />
                <span class="2xs:ml-2 ml-1 min-w-0">
                  <span :class="['block truncate', selected && 'font-semibold']">
                    {{ searchItem.location.name }}
                  </span>
                  <span :class="['block truncate text-xs', active ? 'text-white/80' : 'text-gray-500']">
                    {{ searchItem.location.typeLabel }}
                  </span>
                </span>
              </li>
            </ComboboxOption>
          </template>
          <template v-if="suggestions.projects.length > 0">
            <li class="px-2 pt-2 pb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase select-none">{{ t('views.search.sections.projects') }}</li>
            <ComboboxOption v-for="entity in suggestions.projects" :key="`project-${entity.id}`" v-slot="{ active, selected }" :value="entity" as="template">
              <li :class="['2xs:text-base relative flex cursor-pointer items-center rounded-sm p-2 text-sm select-none', active ? 'bg-ph-green-600 text-white' : 'text-gray-900']">
                <HomeModernIcon aria-hidden="true" class="2xs:h-6 2xs:w-6 h-5 w-5 shrink-0" />
                <span class="2xs:ml-2 ml-1 min-w-0">
                  <span :class="['block truncate', selected && 'font-semibold']">
                    {{ entity.name }}
                  </span>
                </span>
              </li>
            </ComboboxOption>
          </template>
          <template v-if="suggestions.builders.length > 0">
            <li class="px-2 pt-2 pb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase select-none">{{ t('views.search.sections.builders') }}</li>
            <ComboboxOption v-for="entity in suggestions.builders" :key="`builder-${entity.id}`" v-slot="{ active, selected }" :value="entity" as="template">
              <li :class="['2xs:text-base relative flex cursor-pointer items-center rounded-sm p-2 text-sm select-none', active ? 'bg-ph-green-600 text-white' : 'text-gray-900']">
                <BuildingOffice2Icon aria-hidden="true" class="2xs:h-6 2xs:w-6 h-5 w-5 shrink-0" />
                <span class="2xs:ml-2 ml-1 min-w-0">
                  <span :class="['block truncate', selected && 'font-semibold']">
                    {{ entity.name }}
                  </span>
                </span>
              </li>
            </ComboboxOption>
          </template>
        </ul>
      </ComboboxOptions>
    </div>
  </Combobox>
</template>

<style lang="css" scoped></style>
